<template>
  <!-- Empty state is simply no banner (issue #223: clean empty state, no error). -->
  <section
    v-if="hasAnnouncements"
    class="flex flex-col gap-2 px-4 pt-4"
    data-testid="announcement-banner"
    aria-label="Announcements"
  >
    <article
      v-for="a in announcements"
      :key="a.id"
      :data-testid="`announcement-${a.id}`"
      class="flex items-start gap-3 rounded-lg border p-3"
      :class="style(a.severity).container"
    >
      <i :class="[style(a.severity).icon, 'pi mt-0.5 text-base']"></i>

      <div class="min-w-0 flex-1">
        <p class="text-sm font-semibold">{{ a.title }}</p>
        <p class="mt-0.5 whitespace-pre-line text-sm opacity-90">{{ a.body }}</p>
        <a
          v-if="a.cta_label && a.cta_url"
          :href="a.cta_url"
          target="_blank"
          rel="noopener noreferrer"
          class="mt-2 inline-block text-sm font-medium underline"
        >{{ a.cta_label }}</a>
      </div>

      <div class="flex shrink-0 items-center gap-1">
        <Button
          v-if="a.requires_ack"
          label="Acknowledge"
          size="small"
          text
          :loading="busyId === a.id"
          :data-testid="`announcement-ack-${a.id}`"
          @click="onAcknowledge(a.id)"
        />
        <Button
          v-if="a.dismissible"
          icon="pi pi-times"
          size="small"
          text
          rounded
          aria-label="Dismiss announcement"
          :disabled="busyId === a.id"
          :data-testid="`announcement-dismiss-${a.id}`"
          @click="onDismiss(a.id)"
        />
      </div>
    </article>
  </section>
</template>

<script setup lang="ts">
import { useToast } from 'primevue/usetoast';
import { useAnnouncements } from '@/composables/useAnnouncements';

const {
  announcements, hasAnnouncements, acknowledge, dismiss,
} = useAnnouncements();
const toast = useToast();

const busyId = ref<string | null>(null);

// Known severities map to a colour; anything else falls back to a neutral info look.
const SEVERITY_STYLES: Record<string, { container: string; icon: string }> = {
  info: { container: 'border-blue-200 bg-blue-50 text-blue-800', icon: 'pi-info-circle' },
  success: { container: 'border-green-200 bg-green-50 text-green-800', icon: 'pi-check-circle' },
  warning: { container: 'border-amber-200 bg-amber-50 text-amber-800', icon: 'pi-exclamation-triangle' },
  danger: { container: 'border-red-200 bg-red-50 text-red-800', icon: 'pi-exclamation-circle' },
  critical: { container: 'border-red-200 bg-red-50 text-red-800', icon: 'pi-exclamation-circle' },
  error: { container: 'border-red-200 bg-red-50 text-red-800', icon: 'pi-exclamation-circle' },
};

const style = (severity: string) => SEVERITY_STYLES[severity?.toLowerCase()] ?? SEVERITY_STYLES.info!;

const run = async(id: string, action: (id: string) => Promise<void>, verb: string) => {
  if (busyId.value) return;
  busyId.value = id;
  try {
    await action(id);
  } catch (e) {
    toast.add({
      severity: 'error', summary: 'Something went wrong', detail: `Could not ${verb} the announcement. Please try again.`, life: 3000,
    });
  } finally {
    busyId.value = null;
  }
};

const onAcknowledge = (id: string) => run(id, acknowledge, 'acknowledge');
const onDismiss = (id: string) => run(id, dismiss, 'dismiss');
</script>
